import { useAuthStore } from "../auth";
import type { ApiError } from "./types";

// aether-server control plane (see docs/01-backend-architecture.md).
// Env-aware base: in PRODUCTION the SPA is same-origin with aether, which serves
// `/v1` directly. The `/api` prefix exists ONLY for the dev vite proxy
// (vite.config.ts), so in dev we hit `/api/v1`. `import.meta.env.DEV` is
// statically inlined by vite, so the unused branch is tree-shaken per build.
//
// BASE_PATH lets the SPA be mounted under a gateway prefix (e.g. /apps/aether/
// behind Beast/Kalista): Vite's BASE_URL is "/" when VITE_BASE is unset, so
// BASE_PATH is "" and the API base is byte-identical to standalone. Under a
// prefix it becomes e.g. `/apps/aether/v1`; Kalista strips `/apps/aether`
// before proxying, so aether still sees its native `/v1` routes.
const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, "");
const API_BASE = `${BASE_PATH}${import.meta.env.DEV ? "/api/v1" : "/v1"}`;

// ─── Federation overrides (M5) ──────────────────────────────────────────────
// When aether is mounted chrome-less inside the beast-shell host (see
// src/federation/module.tsx) it must NOT derive its API base from BASE_URL and
// must NOT run its own /login redirect on 401 — the host owns auth (same-origin
// OIDC session) and the API prefix. Both stay `null` in standalone mode, so the
// standalone code paths below are byte-identical. `configureFederation()` is
// called once from the federated mount before any request fires.
let federatedApiBase: string | null = null;
let onUnauthorized: (() => void) | null = null;

/**
 * Wire the client to the live host context when federated. `hostApiBase` is the
 * host-provided same-origin prefix (e.g. "/apps/aether/api/"); aether's native
 * API namespace is `/v1`, and Kalista strips the `/apps/aether` app prefix, so
 * we take the app root and append `/v1` → "/apps/aether/v1", which reaches
 * aether-server's `/v1` routes. `unauthorized` is invoked instead of the
 * standalone /login redirect when a request 401s (host raises its auth-needed UI).
 */
export function configureFederation(opts: {
  hostApiBase: string;
  unauthorized: () => void;
}): void {
  const appRoot = opts.hostApiBase.replace(/\/(api\/?)?$/, "");
  federatedApiBase = `${appRoot}/v1`;
  onUnauthorized = opts.unauthorized;
}

/** The active API base: host-provided when federated, else the standalone base. */
function apiBase(): string {
  return federatedApiBase ?? API_BASE;
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError | unknown,
  ) {
    super(
      typeof body === "object" && body && "message" in body && typeof (body as ApiError).message === "string"
        ? (body as ApiError).message
        : `HTTP ${status}`,
    );
    this.name = "HttpError";
  }
}

export type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface RequestOpts {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * The OIDC session's access_token, the production Bearer. It is held in memory
 * only: the auth bootstrap reads it from `GET /auth/me` (cookie session) on every
 * full page load, so it never needs to outlive the page, and keeping it out of
 * `localStorage` keeps it away from any other script on the origin.
 */
let sessionBearer: string | undefined;

/** Set (or clear, with `undefined`) the in-memory session Bearer. */
export function setSessionBearer(token: string | undefined): void {
  sessionBearer = token;
}

/**
 * Dev-only bearer token, DEV builds only. In local dev (static JWKS, no
 * identity service) a token minted by `aetherctl dev-mint-token` is supplied
 * via `VITE_DEV_TOKEN` (or `localStorage["aether:token"]`) so the SPA can call
 * the auth-enforcing dev server. Production never reads it: up to v0.1.7 the
 * bootstrap stored the session token under that same key, and the stored copy
 * was then mistaken for a dev token (the sign-in loop).
 */
function devBearer(): string | undefined {
  if (!import.meta.env.DEV) return undefined;
  try {
    const ls = typeof localStorage !== "undefined" ? localStorage.getItem("aether:token") : null;
    if (ls) return ls;
  } catch {
    // ignore
  }
  return (import.meta.env.VITE_DEV_TOKEN as string | undefined) || undefined;
}

/** Thin typed fetch wrapper. 2xx → JSON; 204 → undefined; else HttpError. */
export async function apiFetch<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const bearer = sessionBearer ?? devBearer();
  const res = await fetch(`${apiBase()}${path}`, {
    method: opts.method ?? "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (!res.ok) {
    // An expired/dead OIDC session surfaces as 401 on every call. Clear the
    // local auth state and bounce to the login route so the user can
    // re-authenticate, instead of being trapped in a permanent error state.
    // No token-refresh loop: we simply drop the session and let the user
    // start a fresh OIDC redirect.
    if (res.status === 401) handleUnauthorized();
    const body: unknown = await res.json().catch(() => null);
    throw new HttpError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Guards against repeated redirects when several queries 401 at once. */
let redirectingToLogin = false;

/**
 * On a 401, clear LOCAL auth state and navigate to /login exactly once. The
 * `redirectingToLogin` guard now covers the WHOLE path (state teardown +
 * navigation), so a fan-out of concurrent 401s clears state once and navigates
 * once — never N times. We pass `reason: "unauthorized"` so logout does NOT fire
 * the RP-initiated fleet-wide logout redirect, which would race this /login
 * navigation. Explicit "Sign out" (logout with no reason) still ends the IdP
 * session fleet-wide. (W1)
 */
function handleUnauthorized(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  // Federated: the host owns the OIDC session and its own login surface — hand
  // the 401 back to it (it raises the auth-needed UI) instead of clearing local
  // state and navigating to aether's standalone /login, which does not exist in
  // the shell. The whole-path guard still coalesces a fan-out of concurrent 401s.
  if (onUnauthorized) {
    onUnauthorized();
    return;
  }
  try {
    useAuthStore.getState().logout({ reason: "unauthorized" });
  } catch {
    // ignore — store may be unavailable in non-browser contexts (tests)
  }
  // End the server-side cookie session too, BEFORE navigating. This is the local
  // `POST /auth/logout`, not the RP-initiated logout. The session outlives its
  // access_token (8 h vs the token's own exp), so after a 401 from an expired
  // token `/auth/me` would keep answering 200 with that dead token: /login would
  // send the "signed-in" operator back into the app, which 401s again, forever.
  void oidcLogout().finally(() => {
    if (typeof window === "undefined") return;
    // Already on the login surface — nothing to navigate to. BASE_PATH keeps this
    // correct under a gateway prefix (e.g. /apps/aether/login); "" when standalone.
    if (window.location.pathname === `${BASE_PATH}/login`) return;
    window.location.assign(`${BASE_PATH}/login`);
  });
}

/**
 * Browser-side OIDC login redirect. Defers authorization_code + PKCE to the
 * proximi.io identity service (ES256). The callback path is operator-registered.
 * organization_id from the ID-token claim drives the tenant context.
 */
export function oidcLoginRedirect(): void {
  window.location.href = `${API_BASE}/auth/oidc/login`;
}

/** The cookie-authenticated session payload returned by `GET /v1/auth/me`. */
export interface OidcSession {
  access_token: string;
  sub: string;
  email: string | null;
  roles: string[];
}

/**
 * Bootstrap the OIDC session: the access_token lives in a server-side session
 * keyed by an HttpOnly cookie (kept out of the URL). On 200 we get the
 * access_token (to use as the SPA Bearer) plus the operator (sub/email/roles);
 * `null` on 401 (no/expired session) so the caller shows Login. Sent with
 * `credentials: 'include'` so the session cookie rides along.
 */
export async function fetchOidcSession(): Promise<OidcSession | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/me`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (res.status === 200) return (await res.json()) as OidcSession;
    return null;
  } catch {
    return null;
  }
}

/**
 * End the server-side OIDC session (clears the cookie). Best-effort — failures
 * are swallowed so a network error never blocks the local logout.
 */
export async function oidcLogout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // ignore — local logout proceeds regardless
  }
}

/**
 * OIDC RP-Initiated Logout: a full-page navigation to the server logout endpoint,
 * which clears the server-side session and 302s the browser to identity's
 * end_session_endpoint (ending the IdP session fleet-wide), then back to the app
 * root. Used for "Sign out" in the real OIDC flow (not the dev stub).
 */
export function oidcLogoutRedirect(): void {
  window.location.assign(`${API_BASE}/auth/oidc/logout`);
}
